import { Text } from '@shared/components/text';
import { StandardFreightOrderEquipmentType } from 'clerk_common/enums';
import { motion } from 'framer-motion';
import { SVGProps, useMemo } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

type CapacitySegment = {
  label: string;
  min: number;
  max: number;
  color: string;
};

enum Labels {
  VERY_LOOSE = 'Very Loose Capacity',
  MODERATELY_LOOSE = 'Moderately Loose Capacity',
  SLIGHTLY_LOOSE = 'Slightly Loose Capacity',
  NEUTRAL = 'Neutral Market',
  SLIGHTLY_TIGHT = 'Slightly Tight Capacity',
  MODERATELY_TIGHT = 'Moderately Tight Capacity',
  VERY_TIGHT = 'Very Tight Capacity',
}

export type DATEquipmentType = Extract<
  StandardFreightOrderEquipmentType,
  'DRY_VAN' | 'REEFER' | 'FLATBED'
>;

const EQUIPMENT_SEGMENTS: Record<DATEquipmentType, CapacitySegment[]> = {
  DRY_VAN: [
    { label: Labels.VERY_LOOSE, min: -100, max: -35, color: '#6C94D9' },
    { label: Labels.MODERATELY_LOOSE, min: -35, max: -30, color: '#8EC6E0' },
    { label: Labels.SLIGHTLY_LOOSE, min: -30, max: -20, color: '#CAECDF' },
    { label: Labels.NEUTRAL, min: -20, max: 10, color: '#FBF6CA' },
    { label: Labels.SLIGHTLY_TIGHT, min: 10, max: 35, color: '#FBDCC7' },
    { label: Labels.MODERATELY_TIGHT, min: 35, max: 45, color: '#F6B59A' },
    { label: Labels.VERY_TIGHT, min: 45, max: 100, color: '#D96975' },
  ],
  REEFER: [
    { label: Labels.VERY_LOOSE, min: -100, max: -36, color: '#6C94D9' },
    { label: Labels.MODERATELY_LOOSE, min: -36, max: -25, color: '#8EC6E0' },
    { label: Labels.SLIGHTLY_LOOSE, min: -25, max: -15, color: '#CAECDF' },
    { label: Labels.NEUTRAL, min: -15, max: 10, color: '#FBF6CA' },
    { label: Labels.SLIGHTLY_TIGHT, min: 10, max: 43, color: '#FBDCC7' },
    { label: Labels.MODERATELY_TIGHT, min: 43, max: 85, color: '#F6B59A' },
    { label: Labels.VERY_TIGHT, min: 45, max: 100, color: '#D96975' },
  ],
  FLATBED: [
    { label: Labels.VERY_LOOSE, min: -100, max: -36, color: '#6C94D9' },
    { label: Labels.MODERATELY_LOOSE, min: -36, max: -25, color: '#8EC6E0' },
    { label: Labels.SLIGHTLY_LOOSE, min: -25, max: -15, color: '#CAECDF' },
    { label: Labels.NEUTRAL, min: -15, max: 35, color: '#FBF6CA' },
    { label: Labels.SLIGHTLY_TIGHT, min: 35, max: 60, color: '#FBDCC7' },
    { label: Labels.MODERATELY_TIGHT, min: 60, max: 94, color: '#F6B59A' },
    { label: Labels.VERY_TIGHT, min: 94, max: 100, color: '#D96975' },
  ],
};

type CustomTooltipProps = {
  active?: boolean;
  payload?: Array<{ payload?: CapacitySegment }>;
};

const CustomTooltip = (props: CustomTooltipProps) => {
  if (!props.active) return null;
  const label = (props.payload?.[0]?.payload as CapacitySegment | undefined)
    ?.label;
  if (!label) return null;

  return (
    <div className="gap-1 rounded-md bg-white px-3 py-1 shadow-md">
      <Text type="body-xs">{label}</Text>
    </div>
  );
};

const AnimatedReferenceLine = (
  props: SVGProps<SVGLineElement> & { score: number }
) => {
  const { x1, y1, y2, score } = props;

  if (!x1 || !y1) return null;

  return (
    <motion.g
      initial={{ x: 0, opacity: 0 }}
      animate={{ x: x1, opacity: 1 }}
      transition={{ duration: 0.7 }}
    >
      <line x1={0} x2={0} y1={y1} y2={y2} stroke="#64748B" strokeWidth={2} />

      <foreignObject x={-20} width={40} height={12} className="text-center">
        <Text type="body-xs" isHeavy>
          {score}
        </Text>
      </foreignObject>
    </motion.g>
  );
};

export const DATMCICapacityBar = ({
  mciScore,
  equipmentType = 'DRY_VAN',
}: {
  mciScore: number;
  equipmentType?: DATEquipmentType;
}) => {
  const segments = EQUIPMENT_SEGMENTS[equipmentType];

  const activeLabel = useMemo(() => {
    const activeSegmentIndex = segments.findIndex(
      (segment) => mciScore >= segment.min && mciScore <= segment.max
    );

    return segments[activeSegmentIndex]?.label;
  }, [mciScore, segments]);

  return (
    <div>
      <ResponsiveContainer width="100%" height={24}>
        <BarChart
          data={segments}
          margin={{ top: 14, right: 0, left: 0, bottom: 0 }}
          barCategoryGap={0}
        >
          <XAxis hide dataKey="label" />
          <YAxis hide domain={[0, 1]} />
          <Tooltip content={<CustomTooltip />} cursor={false} />
          <Bar cursor="pointer" dataKey={() => 1} isAnimationActive={false}>
            {segments.map((entry, index) => (
              <Cell key={index} fill={entry.color} />
            ))}
          </Bar>
          <ReferenceLine
            x={activeLabel}
            stroke="none"
            shape={(props) => (
              <AnimatedReferenceLine {...props} score={mciScore} />
            )}
          />
        </BarChart>
      </ResponsiveContainer>
      <div className="flex justify-between">
        <Text type="body-sm">Loose capacity</Text>
        <Text type="body-sm">Tight capacity</Text>
      </div>
    </div>
  );
};
